import { type ObjectDirective } from 'vue';

/**
 * This directive adds a class to the element when it's scrollable on the X or Y axis.
 * The class is added only when the element is scrollable. (scroll size > client size)
 *
 * ```html
 *  <div v-has-scroll>   <!-- adds the class when scrollable on X or Y -->
 * ```
 *
 * ```css
 *  .v-has-scroll { color: red; }
 *  .v-has-scroll-x { color: red; }
 *  .v-has-scroll-y { color: red; }
 * ```
 */
export const vHasScroll: ObjectDirective<HTMLElement> = {
  mounted(el, { dir }) {
    const ro = new ResizeObserver(() => {
      const scrollableOnX = el.scrollWidth > el.clientWidth;
      const scrollableOnY = el.scrollHeight > el.clientHeight;
      const scrollable = scrollableOnX || scrollableOnY;

      // set the tabindex to 0 if the element is scrollable, so it can be focused
      el.tabIndex = scrollable ? 0 : -1;
      // set the classes
      el.classList.toggle('v-has-scroll', scrollable);
      el.classList.toggle('v-has-scroll-x', scrollableOnX);
      el.classList.toggle('v-has-scroll-y', scrollableOnY);
    });
    ro.observe(el);
    // disconnect the observer when the element is unmounted
    dir.unmounted = () => ro.disconnect();
  },
};
